/**
 * SFX voice pool: a fixed set of pre-built channels (gain → stereo panner → bus input), created once per
 * AudioContext. The panner must see a mono sound as mono (equal-power law, as in every browser): the voice and layer
 * gains keep their input's layout (`makeGain`). A sound borrows a channel, plays up to two buffer sources through it (synth + file layer) and
 * returns it when the sources end. Caps:
 *   - per sound id (`maxVoices`): the oldest voice of that id is stolen;
 *   - globally (pool size): the lowest-priority, oldest voice is stolen — unless every voice outranks the new
 *     sound, in which case the new sound is dropped (a footstep never cuts a touchdown fanfare).
 * Stolen voices fade out over a few milliseconds (no clicks). Sources are disconnected on `ended`: no leaks.
 */

/** Fade applied when a voice is stolen (s). The new sound starts after it. */
export const STEAL_FADE_S = 0.008;

export interface VoiceRequest {
  id: string;
  priority: number;
  maxPerId: number;
  /** Buffers to play together (1 or 2) and their gains. */
  buffers: readonly AudioBuffer[];
  gains: readonly number[];
  volume: number;
  rate: number;
  pan: number;
}

interface Channel {
  readonly gain: GainNode;
  readonly panner: StereoPannerNode | null;
  readonly srcs: (AudioBufferSourceNode | null)[];
  /** Per-layer gain nodes (only for layers whose gain ≠ 1), parallel to `srcs`. */
  readonly extras: (GainNode | null)[];
  id: string;
  priority: number;
  startedAt: number;
  endsAt: number;
  /** Bumped every time the channel is (re)assigned: stale `ended` callbacks are ignored. */
  token: number;
  busy: boolean;
  live: number;
}

export class VoicePool {
  private readonly channels: Channel[] = [];

  private readonly makeGain: () => GainNode;

  constructor(
    private readonly ctx: BaseAudioContext,
    dest: AudioNode,
    size: number,
    /**
     * Builds the voice and layer gains. They must keep the source's channel layout (WebAudio's default `max`
     * mode) so a mono sound reaches its StereoPanner as mono and pans equal-power, like the web: see
     * `AudioContextLike.createVoiceGain`. Default `ctx.createGain()`.
     */
    makeGain?: () => GainNode,
  ) {
    this.makeGain = makeGain ?? (() => ctx.createGain());
    for (let i = 0; i < size; i++) {
      const gain = this.makeGain();
      gain.gain.value = 0;
      let panner: StereoPannerNode | null = null;
      if (typeof ctx.createStereoPanner === 'function') {
        panner = ctx.createStereoPanner();
        gain.connect(panner);
        panner.connect(dest);
      } else gain.connect(dest);
      this.channels.push({
        gain,
        panner,
        srcs: [null, null],
        extras: [null, null],
        id: '',
        priority: 0,
        startedAt: 0,
        endsAt: 0,
        token: 0,
        busy: false,
        live: 0,
      });
    }
  }

  get size(): number {
    return this.channels.length;
  }

  /** Voices currently sounding. */
  get active(): number {
    let n = 0;
    for (const c of this.channels) if (c.busy) n++;
    return n;
  }

  /** Voices of one id currently sounding. */
  activeOf(id: string): number {
    let n = 0;
    for (const c of this.channels) if (c.busy && c.id === id) n++;
    return n;
  }

  /** Start a sound now. Returns false when it was dropped (pool full of higher-priority voices). */
  play(req: VoiceRequest): boolean {
    const now = this.ctx.currentTime;
    let ch: Channel | null = null;
    // 1. per-id cap: steal the oldest voice of the same id
    if (this.activeOf(req.id) >= req.maxPerId) {
      for (const c of this.channels) {
        if (c.busy && c.id === req.id && (!ch || c.startedAt < ch.startedAt)) ch = c;
      }
    }
    // 2. a free channel
    if (!ch)
      for (const c of this.channels)
        if (!c.busy) {
          ch = c;
          break;
        }
    // 3. global cap: lowest priority, then oldest
    if (!ch) {
      for (const c of this.channels) {
        if (!ch || c.priority < ch.priority || (c.priority === ch.priority && c.startedAt < ch.startedAt))
          ch = c;
      }
      if (!ch || ch.priority > req.priority) return false;
    }
    const stolen = ch.busy;
    const t = stolen ? now + STEAL_FADE_S : now;
    const g = ch.gain.gain;
    if (stolen) {
      g.cancelScheduledValues(now);
      g.setValueAtTime(g.value, now);
      g.linearRampToValueAtTime(0, t);
      for (let i = 0; i < ch.srcs.length; i++) {
        const s = ch.srcs[i];
        if (!s) continue;
        try {
          s.stop(t);
        } catch {
          /* already stopped */
        }
        ch.srcs[i] = null; // its own onended handler disconnects it (and its layer gain)
        ch.extras[i] = null;
      }
    } else {
      g.cancelScheduledValues(now);
    }
    g.setValueAtTime(req.volume, t);
    if (ch.panner) {
      ch.panner.pan.cancelScheduledValues(now);
      ch.panner.pan.setValueAtTime(req.pan, t);
    }
    const token = ++ch.token;
    ch.busy = true;
    ch.id = req.id;
    ch.priority = req.priority;
    ch.startedAt = t;
    ch.live = 0;
    let longest = 0;
    for (let i = 0; i < req.buffers.length && i < ch.srcs.length; i++) {
      const buf = req.buffers[i]!;
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.playbackRate.value = req.rate;
      const layerGain = req.gains[i] ?? 1;
      let tail: AudioNode = src;
      let extra: GainNode | null = null;
      if (layerGain !== 1) {
        extra = this.makeGain();
        extra.gain.value = layerGain;
        src.connect(extra);
        tail = extra;
      }
      tail.connect(ch.gain);
      const channel = ch;
      src.onended = () => {
        src.onended = null;
        src.disconnect();
        extra?.disconnect();
        if (channel.token !== token) return;
        const idx = channel.srcs.indexOf(src);
        if (idx >= 0) {
          channel.srcs[idx] = null;
          channel.extras[idx] = null;
        }
        if (--channel.live <= 0) this.release(channel);
      };
      ch.srcs[i] = src;
      ch.extras[i] = extra;
      ch.live++;
      src.start(t);
      longest = Math.max(longest, buf.duration / req.rate);
    }
    ch.endsAt = t + longest;
    if (ch.live === 0) this.release(ch);
    return true;
  }

  /** Stop everything quickly (e.g. leaving a match). */
  stopAll(fade = 0.03): void {
    const now = this.ctx.currentTime;
    for (const c of this.channels) {
      if (!c.busy) continue;
      const g = c.gain.gain;
      g.cancelScheduledValues(now);
      g.setValueAtTime(g.value, now);
      g.linearRampToValueAtTime(0, now + fade);
      for (const s of c.srcs) {
        try {
          s?.stop(now + fade);
        } catch {
          /* already stopped */
        }
      }
    }
  }

  dispose(): void {
    for (const c of this.channels) {
      c.token++;
      for (let i = 0; i < c.srcs.length; i++) {
        const s = c.srcs[i];
        if (!s) continue;
        s.onended = null;
        try {
          s.stop();
        } catch {
          /* already stopped */
        }
        s.disconnect();
        c.extras[i]?.disconnect();
        c.srcs[i] = null;
        c.extras[i] = null;
      }
      c.busy = false;
      c.gain.disconnect();
      c.panner?.disconnect();
    }
    this.channels.length = 0;
  }

  private release(c: Channel): void {
    c.busy = false;
    c.live = 0;
    c.id = '';
    c.priority = 0;
  }
}
